/**
 * Weather for an event's venue, via Open-Meteo (free, no API key, CORS-enabled).
 * Called directly from the client with the event's resolved lat/lng. Returns the
 * current conditions plus a daily forecast for the event's days that fall inside
 * the provider's ~16-day forecast horizon.
 */

export interface CurrentWeather {
  temperatureF: number;
  windMph: number;
  code: number;
  description: string;
}

export interface DailyWeather {
  /** YYYY-MM-DD. */
  date: string;
  highF: number;
  lowF: number;
  /** Max precipitation probability (%), or null when the provider omits it. */
  precipChance: number | null;
  code: number;
  description: string;
}

export interface WeatherResult {
  current: CurrentWeather | null;
  daily: DailyWeather[];
  /** True when some event days are beyond the forecast horizon (show "closer to the date"). */
  forecastBeyondRange: boolean;
}

/** WMO weather codes → short human descriptions (Open-Meteo's `weather_code`). */
const WMO_DESCRIPTIONS: Record<number, string> = {
  0: "Clear",
  1: "Mainly clear",
  2: "Partly cloudy",
  3: "Overcast",
  45: "Fog",
  48: "Rime fog",
  51: "Light drizzle",
  53: "Drizzle",
  55: "Heavy drizzle",
  56: "Freezing drizzle",
  57: "Freezing drizzle",
  61: "Light rain",
  63: "Rain",
  65: "Heavy rain",
  66: "Freezing rain",
  67: "Freezing rain",
  71: "Light snow",
  73: "Snow",
  75: "Heavy snow",
  77: "Snow grains",
  80: "Rain showers",
  81: "Rain showers",
  82: "Violent rain showers",
  85: "Snow showers",
  86: "Heavy snow showers",
  95: "Thunderstorm",
  96: "Thunderstorm w/ hail",
  99: "Severe thunderstorm",
};

export const describeWeatherCode = (code: number): string =>
  WMO_DESCRIPTIONS[code] ?? "—";

const FORECAST_HORIZON_DAYS = 15;

const addDaysIso = (isoDate: string, days: number): string => {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

export interface ForecastWindow {
  /** Clamped daily range to request, or null when none of the event falls in range. */
  dailyStart: string | null;
  dailyEnd: string | null;
  /** True when the event extends past the forecast horizon. */
  beyondRange: boolean;
}

/**
 * The daily-forecast range to actually request: the event's days clamped to
 * [today, today + horizon]. Past days are dropped (forecast is forward-looking);
 * days beyond the horizon flag `beyondRange` so the UI can explain the gap.
 */
export function forecastWindow(
  eventStartDate: string,
  eventEndDate: string,
  today: string,
): ForecastWindow {
  const horizon = addDaysIso(today, FORECAST_HORIZON_DAYS);
  const beyondRange = eventEndDate > horizon;
  const start = eventStartDate < today ? today : eventStartDate;
  const end = eventEndDate > horizon ? horizon : eventEndDate;
  if (start > end) {
    return { dailyStart: null, dailyEnd: null, beyondRange };
  }
  return { dailyStart: start, dailyEnd: end, beyondRange };
}

/**
 * Fetch current + (clamped) daily forecast for a venue. `eventStartDate` /
 * `eventEndDate` are inclusive YYYY-MM-DD. Throws on network/HTTP failure so the
 * caller can show a "weather unavailable" state.
 */
export async function getWeather(
  latitude: number,
  longitude: number,
  eventStartDate: string,
  eventEndDate: string,
): Promise<WeatherResult> {
  const today = new Date().toISOString().slice(0, 10);
  const win = forecastWindow(eventStartDate, eventEndDate, today);

  const params = new URLSearchParams({
    latitude: String(latitude),
    longitude: String(longitude),
    current: "temperature_2m,weather_code,wind_speed_10m",
    temperature_unit: "fahrenheit",
    wind_speed_unit: "mph",
    precipitation_unit: "inch",
    timezone: "auto",
  });
  if (win.dailyStart && win.dailyEnd) {
    params.set(
      "daily",
      "weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max",
    );
    params.set("start_date", win.dailyStart);
    params.set("end_date", win.dailyEnd);
  }

  const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params.toString()}`);
  if (!res.ok) {
    throw new Error(`Weather request failed (${res.status})`);
  }
  const data = await res.json();

  const current: CurrentWeather | null = data.current
    ? {
        temperatureF: Math.round(data.current.temperature_2m),
        windMph: Math.round(data.current.wind_speed_10m),
        code: data.current.weather_code,
        description: describeWeatherCode(data.current.weather_code),
      }
    : null;

  const daily: DailyWeather[] = [];
  const d = data.daily;
  if (d?.time?.length) {
    for (let i = 0; i < d.time.length; i++) {
      daily.push({
        date: d.time[i],
        highF: Math.round(d.temperature_2m_max?.[i]),
        lowF: Math.round(d.temperature_2m_min?.[i]),
        precipChance: d.precipitation_probability_max?.[i] ?? null,
        code: d.weather_code?.[i],
        description: describeWeatherCode(d.weather_code?.[i]),
      });
    }
  }

  return { current, daily, forecastBeyondRange: win.beyondRange };
}
